/**
 * Observe Page — Unified observability: executions, costs, audit trail.
 * Replaces standalone Audit, Cost Analytics, and Testing tabs.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Bot,
  Activity,
  DollarSign,
  FileText,
  Search,
  RefreshCw,
  Download,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Shield,
  ArrowUp,
  ArrowDown,
  Loader2,
  Sparkles,
  Network,
  Zap,
  TrendingUp,
  Filter,
  Blocks,
} from 'lucide-react';
import { cpFetch } from '@/lib/cpClient';
// agentClient removed — using cpFetch for auth-safe discovery
import { formatCost, estimateWorkflowCost } from '../volcano/utils/cost-calculator';
import { dedupeExecutions, getWorkflowSteps, measureFailed, observeCoverageNote } from '../agentHonesty';

type Section = 'executions' | 'costs' | 'audit';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

const eventIcons: Record<string, React.ReactNode> = {
  execution: <Activity className="w-3.5 h-3.5" />,
  promotion: <ArrowUp className="w-3.5 h-3.5" />,
  rollback: <ArrowDown className="w-3.5 h-3.5" />,
  permission_denied: <Shield className="w-3.5 h-3.5" />,
  security_alert: <AlertTriangle className="w-3.5 h-3.5" />,
};

const eventColors: Record<string, string> = {
  execution: 'bg-sky-100 text-sky-700',
  promotion: 'bg-emerald-100 text-emerald-700',
  rollback: 'bg-amber-100 text-amber-700',
  permission_denied: 'bg-orange-100 text-orange-700',
  security_alert: 'bg-red-100 text-red-700',
};

const statusColors: Record<string, string> = {
  success: 'bg-emerald-100 text-emerald-700',
  failure: 'bg-red-100 text-red-700',
  warning: 'bg-amber-100 text-amber-700',
};


/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ObservePage() {
  const navigate = useNavigate();
  const [section, setSection] = useState<Section>('executions');

  // Execution state
  const [recentExecs, setRecentExecs] = useState<any[]>([]);
  const [serverCoverageNote, setServerCoverageNote] = useState<string | null>(null);
  const [execLoading, setExecLoading] = useState(false);

  // Audit state
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('all');

  // Cost state
  const [costAgents, setCostAgents] = useState<AgentInfo[]>([]);
  const [costLoading, setCostLoading] = useState(false);

  const activeTab = 'observe';

  useEffect(() => {
    if (section === 'executions') loadRecentExecs();
    if (section === 'audit') loadAuditLogs();
    if (section === 'costs') loadCostData();
  }, [section]);

  /* ---------- Loaders ---------- */

  const loadRecentExecs = async () => {
    setExecLoading(true);
    try {
      const res = await cpFetch('/api/agents/executions?limit=30');
      setRecentExecs(dedupeExecutions(res.executions || res.history || []));
      setServerCoverageNote(typeof res.coverage_note === 'string' ? res.coverage_note : null);
    } catch {
      setRecentExecs([]);
      setServerCoverageNote(null);
    }
    finally { setExecLoading(false); }
  };

  const loadAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (eventFilter !== 'all') params.append('event_type', eventFilter);
      const res = await cpFetch(`/api/agents/audit?${params}`);
      setAuditLogs(res.logs || []);
    } catch { setAuditLogs([]); }
    finally { setAuditLoading(false); }
  };

  const loadCostData = async () => {
    setCostLoading(true);
    try {
      const res = await cpFetch('/api/agents/discover');
      const all = Array.isArray(res?.agents) ? res.agents : [];
      setCostAgents(all.filter((a: any) => a.execution_engine === 'volcano'));
    } catch { setCostAgents([]); }
    finally { setCostLoading(false); }
  };

  const handleExportAudit = async () => {
    try {
      const res = await cpFetch('/api/agents/audit/export');
      const blob = new Blob([JSON.stringify(res.logs || [], null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(`Export failed: ${err.message}`);
    }
  };

  /* ---------- Cost derived ---------- */

  const costData = useMemo(() => {
    return costAgents.map((a) => {
      const steps = getWorkflowSteps(a);
      const est = estimateWorkflowCost(steps);
      return { agent: a, cost: est.avg, stepCount: steps.length };
    }).sort((a, b) => b.cost - a.cost);
  }, [costAgents]);

  const totalCost = costData.reduce((s, c) => s + c.cost, 0);

  /* ---------- Audit filtered ---------- */

  const filteredAudit = useMemo(() => {
    if (!auditSearch) return auditLogs;
    const q = auditSearch.toLowerCase();
    return auditLogs.filter((l: any) =>
      (l.agent_name || '').toLowerCase().includes(q) ||
      (l.agent_id || '').toLowerCase().includes(q)
    );
  }, [auditLogs, auditSearch]);

  /* ---------- Render ---------- */

  const sections: { id: Section; label: string; icon: React.ElementType }[] = [
    { id: 'executions', label: 'Executions', icon: Activity },
    { id: 'costs', label: 'Costs', icon: DollarSign },
    { id: 'audit', label: 'Audit Trail', icon: FileText },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* ── Top Navigation Bar ─────────────────────────────────── */}
      <div className="border-b bg-white px-6 pt-4 pb-0">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-violet-600 flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Agent Manager</h1>
            <p className="text-xs text-gray-500">Build, run, and observe your agentic workflows</p>
          </div>
        </div>

        <nav className="flex gap-1 -mb-px">
          {[
            { id: 'hub', label: 'Agents', icon: Bot, path: '/agents/hub' },
            { id: 'create', label: 'Create', icon: Sparkles, path: '/agents/create' },
            { id: 'observe', label: 'Observe', icon: Activity, path: '/agents/observe' },
            { id: 'ecosystem', label: 'Ecosystem', icon: Network, path: '/agents/ecosystem' },
            { id: 'steps', label: 'Steps', icon: Blocks, path: '/agents/steps' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-violet-600 text-violet-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Content ───────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto bg-gray-50 p-6">
        {/* Section selector */}
        <div className="flex items-center gap-2 mb-6">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                section === s.id
                  ? 'bg-violet-100 text-violet-700'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border'
              }`}
            >
              <s.icon className="w-4 h-4" />
              {s.label}
            </button>
          ))}
        </div>

        {/* ── Executions Section ─────────────────────────────── */}
        {section === 'executions' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent Executions</h2>
              <Button variant="ghost" size="sm" onClick={loadRecentExecs} disabled={execLoading}>
                <RefreshCw className={`w-4 h-4 ${execLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            {(serverCoverageNote || observeCoverageNote(recentExecs)) && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {serverCoverageNote || observeCoverageNote(recentExecs)}
              </p>
            )}
            {(() => {
              const failed = measureFailed(recentExecs);
              return (
                <p className="text-xs text-gray-500">
                  {failed.measured
                    ? `Failed ${failed.failed} of ${failed.total} recorded runs (deduped).`
                    : 'Failed is unmeasured — no execution rows in this window.'}
                </p>
              );
            })()}

            {execLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                <span className="text-gray-500">Loading executions...</span>
              </div>
            ) : recentExecs.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <Activity className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No recent executions</p>
                  <p className="text-sm mt-1">Run an agent from the Agents tab to see execution history here.</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="divide-y max-h-[600px] overflow-y-auto">
                    {recentExecs.map((exec: any, i: number) => (
                      <div key={exec.execution_id || exec._id || `exec-${i}`} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition-colors">
                        {exec.status === 'success' ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{exec.agent_name || exec.agent_id}</p>
                          <p className="text-xs text-gray-500">
                            {exec.metrics?.duration_ms ? `${exec.metrics.duration_ms}ms` : '—'}
                            {exec.metrics?.total_tokens ? ` · ${exec.metrics.total_tokens} tokens` : ''}
                            {exec.metrics?.estimated_cost ? ` · $${exec.metrics.estimated_cost.toFixed(4)}` : ''}
                          </p>
                        </div>
                        <Badge className={`text-xs ${exec.status === 'success' ? statusColors.success : statusColors.failure}`}>
                          {exec.status}
                        </Badge>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {exec.executed_at ? new Date(exec.executed_at).toLocaleString() : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ── Costs Section ─────────────────────────────────── */}
        {section === 'costs' && (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="bg-white">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Volcano Agents</p>
                    <p className="text-2xl font-bold text-gray-900">{costAgents.length}</p>
                  </div>
                  <Zap className="w-7 h-7 text-amber-500 opacity-60" />
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Avg Cost / Run</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCost(costAgents.length ? totalCost / costAgents.length : 0)}
                    </p>
                  </div>
                  <DollarSign className="w-7 h-7 text-emerald-500 opacity-60" />
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Total Est. Cost</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCost(totalCost)}</p>
                    <p className="text-[10px] text-gray-400">per execution (all agents)</p>
                  </div>
                  <TrendingUp className="w-7 h-7 text-violet-500 opacity-60" />
                </CardContent>
              </Card>
            </div>

            {/* Agent cost list */}
            {costLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                <span className="text-gray-500">Loading cost data...</span>
              </div>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Cost per Agent (estimated per run)</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y max-h-[500px] overflow-y-auto">
                    {costData.map(({ agent, cost, stepCount }, i) => (
                      <div
                        key={agent.id}
                        className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/agents/hub/${agent.id}`)}
                      >
                        <div className="w-7 h-7 rounded-full bg-gray-100 text-gray-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{agent.name}</p>
                          <p className="text-xs text-gray-500">{stepCount} steps · {agent.domain}</p>
                        </div>
                        <Badge variant="secondary" className="text-xs">{agent.deployment_stage}</Badge>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-bold text-gray-900">{formatCost(cost)}</p>
                          <p className="text-[10px] text-gray-400">per run</p>
                        </div>
                      </div>
                    ))}
                    {costData.length === 0 && (
                      <div className="py-8 text-center text-gray-500 text-sm">No Volcano agents found</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ── Audit Section ─────────────────────────────────── */}
        {section === 'audit' && (
          <div className="space-y-4">
            {/* Controls */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Search audit logs..." value={auditSearch} onChange={(e) => setAuditSearch(e.target.value)} className="pl-9 bg-white" />
              </div>
              <select
                value={eventFilter}
                onChange={(e) => { setEventFilter(e.target.value); }}
                className="text-sm border rounded-md px-3 py-2 bg-white"
              >
                <option value="all">All Events</option>
                <option value="execution">Executions</option>
                <option value="promotion">Promotions</option>
                <option value="security">Security</option>
              </select>
              <Button variant="ghost" size="sm" onClick={loadAuditLogs} disabled={auditLoading}>
                <RefreshCw className={`w-4 h-4 ${auditLoading ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportAudit}>
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
            </div>

            {auditLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                <span className="text-gray-500">Loading audit logs...</span>
              </div>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="divide-y max-h-[600px] overflow-y-auto">
                    {filteredAudit.length === 0 ? (
                      <div className="py-12 text-center text-gray-500 text-sm">No audit logs found</div>
                    ) : filteredAudit.map((log: any, i: number) => (
                      <div key={log.id || log._id || i} className="px-5 py-3 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <Badge className={`text-xs ${eventColors[log.event_type] || 'bg-gray-100 text-gray-700'}`}>
                              {eventIcons[log.event_type] || <FileText className="w-3.5 h-3.5" />}
                              <span className="ml-1">{(log.event_type || '').replace(/_/g, ' ')}</span>
                            </Badge>
                            <Badge className={`text-xs ${statusColors[log.status] || 'bg-gray-100 text-gray-700'}`}>
                              {log.status}
                            </Badge>
                          </div>
                          <span className="text-xs text-gray-400">
                            {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-600">
                          <span><strong>Agent:</strong> {log.agent_name || log.agent_id}</span>
                          {log.duration_ms && <span>{log.duration_ms}ms</span>}
                          {log.user_id && <span>User: {log.user_id}</span>}
                        </div>
                        {log.details && Object.keys(log.details).length > 0 && (
                          <details className="mt-1.5">
                            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Details</summary>
                            <pre className="mt-1 text-xs bg-gray-100 rounded p-2 overflow-x-auto max-h-32">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
